import {SheetModal} from 'app/pages/school/Schedule/components/timetable/pages/workLoadBreakDown/modals/components/SheetModal'
import CustomInputSelect from 'common/components/CustomInputSelect'
import {Form, Formik} from 'formik'
import {CalendarDays} from 'lucide-react'
import React from 'react'
import {useIntl} from 'react-intl'

interface LessonPlanEditSheetProps {
  show: boolean
  onHide: () => void
  /** Lesson-planning class key (`branch|||curriculum|||class`) — identity, never rendered raw. */
  className: string
  /** What the title shows for the class (curriculum prefix removed by the parent). */
  classLabel?: string
  subjectName: string
  frequency: string
  duration: string
  onSave: (data: {frequency: string; duration: string}) => void
  isDark?: boolean
}

const parseFrequency = (
  value: string
): {frequencyValue: number | ''; frequencyUnit: 'week' | 'day'} => {
  if (!value) return {frequencyValue: '', frequencyUnit: 'week'}
  const match = value.match(/^(\d+)\/(week|day)$/i)
  if (match) {
    const num = parseInt(match[1], 10)
    return {
      frequencyValue: Number.isFinite(num) ? num : '',
      frequencyUnit: match[2].toLowerCase() as 'week' | 'day',
    }
  }
  const numMatch = value.match(/^(\d+)$/)
  if (numMatch) {
    const num = parseInt(numMatch[1], 10)
    return {frequencyValue: Number.isFinite(num) ? num : '', frequencyUnit: 'week'}
  }
  return {frequencyValue: '', frequencyUnit: 'week'}
}

const parseDuration = (value: string | number): number | '' => {
  const parsed = typeof value === 'number' ? value : parseInt(String(value || ''), 10)
  return Number.isFinite(parsed) && parsed >= 1 ? parsed : ''
}

const LessonPlanEditSheet: React.FC<LessonPlanEditSheetProps> = ({
  show,
  onHide,
  className,
  classLabel,
  subjectName,
  frequency,
  duration,
  onSave,
}) => {
  // The key's last part is the class name; the full key used to leak into the title.
  const titleClass = classLabel ?? className.split('|||').pop() ?? className
  const intl = useIntl()
  const parsedFreq = parseFrequency(frequency)
  const parsedDur = parseDuration(duration)

  const frequencyUnitOptions = [
    {value: 'week', label: intl.formatMessage({id: 'SCHEDULE.TIMETABLE.PER_WEEK'})},
    {value: 'day', label: intl.formatMessage({id: 'SCHEDULE.TIMETABLE.PER_DAY'})},
  ]

  const durationUnitOptions = [
    {value: 'slots', label: intl.formatMessage({id: 'SCHEDULE.TIMETABLE.SLOTS_LABEL'})},
  ]

  const initialValues = {
    frequencyValue: parsedFreq.frequencyValue,
    frequencyUnit: parsedFreq.frequencyUnit,
    durationValue: parsedDur,
    durationUnit: 'slots',
  }

  const handleSubmit = (values: {
    frequencyValue: number | ''
    frequencyUnit: string
    durationValue: number | ''
  }) => {
    const freqStr =
      values.frequencyValue !== '' ? `${values.frequencyValue}/${values.frequencyUnit}` : ''
    onSave({
      frequency: freqStr,
      duration: values.durationValue !== '' ? String(values.durationValue) : '',
    })
    onHide()
  }

  return (
    <SheetModal
      show={show}
      onHide={onHide}
      title={`${titleClass} - ${subjectName}`}
      elementIcon={<CalendarDays size={19} />}
      icon=''
      showCloseButton={false}
    >
      <Formik
        key={`${className}-${subjectName}-${show ? 'open' : 'closed'}`}
        initialValues={initialValues}
        enableReinitialize
        onSubmit={handleSubmit}
      >
        {({setFieldValue, handleSubmit: formikSubmit, dirty}) => (
          <Form className='w-100 d-flex flex-column'>
            {/* Form container - frequency-form-box same as FrequencyEditModal */}
            <div className='frequency-form-box p-4 mb-4'>
              <h2 className='mb-6' style={{color: 'var(--frequency-heading-color)'}}>
                {intl.formatMessage({
                  id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.EDIT_HEADING',
                })}
              </h2>

              {/* Frequency - CustomInputSelect (integrated input + dropdown) */}
              <div className='mb-6'>
                <label className='form-label'>
                  {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.COL.FREQUENCY'})}
                </label>
                <CustomInputSelect
                  name='frequency'
                  inputName='frequencyValue'
                  dropdownName='frequencyUnit'
                  label=''
                  inputType='text'
                  dropdownOptions={frequencyUnitOptions}
                  required
                  showError={false}
                  onChange={(inputVal, dropdownVal) => {
                    const raw = inputVal === null || inputVal === undefined ? '' : String(inputVal)
                    let digits = raw.replace(/\D/g, '')
                    if (digits === '') {
                      setFieldValue('frequencyValue', '')
                    } else {
                      if (digits.length > 1) {
                        digits = digits.replace(/^0+/, '') || '0'
                      }
                      let next = Math.min(10, Math.max(0, parseInt(digits, 10)))
                      if (!Number.isFinite(next)) next = 0
                      setFieldValue('frequencyValue', next)
                    }
                    if (dropdownVal !== undefined) setFieldValue('frequencyUnit', dropdownVal)
                  }}
                  helperText={intl.formatMessage({
                    id: 'SCHEDULE.TIMETABLE.FREQUENCY_HELPER_TEXT',
                  })}
                />
              </div>

              {/* Duration - CustomInputSelect (integrated input + Slots) */}
              <div className='mb-0'>
                <label className='form-label'>
                  {intl.formatMessage({id: 'SCHEDULE.TIMETABLE.DURATION'})}
                </label>
                <CustomInputSelect
                  name='duration'
                  inputName='durationValue'
                  dropdownName='durationUnit'
                  label=''
                  inputType='text'
                  dropdownOptions={durationUnitOptions}
                  required
                  showError={false}
                  onChange={(inputVal) => {
                    const raw = inputVal === null || inputVal === undefined ? '' : String(inputVal)
                    let digits = raw.replace(/\D/g, '')
                    if (digits === '') {
                      setFieldValue('durationValue', '')
                    } else {
                      if (digits.length > 1) {
                        digits = digits.replace(/^0+/, '') || '0'
                      }
                      let next = Math.min(10, Math.max(1, parseInt(digits, 10) || 0))
                      if (!Number.isFinite(next) || next < 1) next = 1
                      setFieldValue('durationValue', next)
                    }
                  }}
                  helperText={intl.formatMessage({id: 'SCHEDULE.TIMETABLE.DURATION_HELPER_TEXT'})}
                />
              </div>
            </div>

            {/* Info box - same structure as reference */}
            <div
              className='mb-4 p-3 rounded'
              style={{
                background: 'var(--bs-gray-100, rgba(225, 240, 255, 0.5))',
                border: '1px solid #009ef7',
                fontSize: '12px',
              }}
            >
              <div className='ms-4 text-muted' style={{fontSize: '11px'}}>
                {intl.formatMessage({
                  id: 'ONBOARDING_WIZARD.STEP4_MANUAL.LESSON_PLANNING.EXAMPLE_TEXT',
                })}
              </div>
            </div>

            {/* Footer buttons */}
            <div className='d-flex justify-content-end align-items-center gap-2 mt-2'>
              <div className='d-flex gap-2'>
                <button type='button' className='btn btn-light' onClick={onHide}>
                  {intl.formatMessage({id: 'GENERAL.CANCEL'})}
                </button>
                <button
                  type='button'
                  className='btn btn-primary'
                  onClick={() => formikSubmit()}
                  // Save stays disabled until frequency or duration actually changed.
                  disabled={!dirty}
                >
                  {intl.formatMessage({id: 'GENERAL.SAVE'})}
                </button>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </SheetModal>
  )
}

export default LessonPlanEditSheet
